// Activity presets — common kids' activities a parent might schedule.
//
// One-tap fillers for the New Event dialog. Each preset stuffs in:
//   * `title`          — the activity name (the parent then prepends
//                        their child's name or anything else they
//                        want — fully editable).
//   * `type`           — the event-type category that drives the
//                        chip colour & filters across all calendar
//                        views.
//   * `durationMin`    — typical session length, applied as
//                        `end = start + durationMin` once the user
//                        sets a start time.
//   * `freq`           — recurrence pattern. "weekly" is the
//                        overwhelming default for kids' activities;
//                        the user can flip it off in the recurrence
//                        section of the dialog before saving.
//
// Presets are deliberately UK-centric copy (e.g. "Brownies",
// "Beavers") because that's the primary audience. They are
// suggestions — every field stays editable post-fill.
//
// Adding a new preset: keep the list short and high-signal. If a
// parent uses an activity weekly enough to deserve a preset, it
// belongs here. Otherwise leave it to the freeform Title field.
export const ACTIVITY_PRESETS = [
  { id: "football",      label: "Football practice",  title: "Football practice",      type: "sports",          durationMin: 60, freq: "weekly" },
  { id: "swimming",      label: "Swimming",           title: "Swimming",               type: "sports",          durationMin: 60, freq: "weekly" },
  { id: "gymnastics",    label: "Gymnastics",         title: "Gymnastics",             type: "sports",          durationMin: 60, freq: "weekly" },
  { id: "karate",        label: "Karate / martial arts", title: "Karate",              type: "sports",          durationMin: 60, freq: "weekly" },
  { id: "dance",         label: "Dance class",        title: "Dance class",            type: "extracurricular", durationMin: 60, freq: "weekly" },
  { id: "music",         label: "Music lesson",       title: "Music lesson",           type: "extracurricular", durationMin: 30, freq: "weekly" },
  { id: "drama",         label: "Drama club",         title: "Drama club",             type: "extracurricular", durationMin: 60, freq: "weekly" },
  { id: "art",           label: "Art class",          title: "Art class",              type: "extracurricular", durationMin: 60, freq: "weekly" },
  { id: "scouts",        label: "Scouts / Cubs / Beavers", title: "Scouts",            type: "extracurricular", durationMin: 90, freq: "weekly" },
  { id: "brownies",      label: "Brownies / Guides / Rainbows", title: "Brownies",     type: "extracurricular", durationMin: 90, freq: "weekly" },
  { id: "tutoring",      label: "Tutoring",           title: "Tutoring",               type: "extracurricular", durationMin: 60, freq: "weekly" },
  { id: "after_school",  label: "After-school club",  title: "After-school club",      type: "school",          durationMin: 90, freq: "weekly" },
];

/**
 * Apply a preset onto an existing form snapshot.
 *
 * Returns a NEW form object — does not mutate. The user's existing
 * start time is preserved if they had one (the `end` is then derived
 * from `start + durationMin`); otherwise both stay blank and the
 * duration kicks in once the user picks a start.
 */
export function applyPresetToForm(form, preset) {
  if (!preset) return form;
  const next = {
    ...form,
    title: preset.title,
    type: preset.type,
    freq: preset.freq || form.freq || "none",
    end_mode: preset.freq && preset.freq !== "none" ? "never" : form.end_mode,
  };
  // If the user already has a start time, compute an end from the
  // preset's duration. We don't OVERWRITE an existing end — they
  // may have set it intentionally.
  if (form.start && !form.end && preset.durationMin) {
    try {
      const s = new Date(form.start);
      const e = new Date(s.getTime() + preset.durationMin * 60 * 1000);
      // datetime-local format: YYYY-MM-DDTHH:mm (no seconds, no TZ).
      const pad = (n) => String(n).padStart(2, "0");
      next.end = `${e.getFullYear()}-${pad(e.getMonth() + 1)}-${pad(e.getDate())}T${pad(e.getHours())}:${pad(e.getMinutes())}`;
    } catch (_err) {
      // If parsing fails just leave end alone — the user will set
      // it manually like any normal event.
    }
  }
  return next;
}
